import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// ─────────────────────────────────────────────────────────────────────────
// Prevent Radix / Recharts / dropdown popovers from emitting the benign
// "ResizeObserver loop completed with undelivered notifications" runtime
// error that pops up in the CRA dev overlay. Three-layer defense:
//   1. Wrap the ResizeObserver callback in requestAnimationFrame so no loop
//      notification is ever emitted (root-cause fix).
//   2. Capture-phase `error` / `unhandledrejection` swallowers as backstop.
//   3. Filter `console.error` in case another overlay hooks it directly.
// ─────────────────────────────────────────────────────────────────────────
if (typeof window !== "undefined") {
  // (1) Root-cause: schedule RO callbacks on next animation frame.
  const NativeRO = window.ResizeObserver;
  if (NativeRO && !NativeRO.__patched__) {
    class PatchedResizeObserver extends NativeRO {
      constructor(callback) {
        super((entries, observer) => {
          window.requestAnimationFrame(() => {
            if (!Array.isArray(entries) || !entries.length) return;
            try {
              callback(entries, observer);
            } catch (_e) {
              /* ignore */
            }
          });
        });
      }
    }
    PatchedResizeObserver.__patched__ = true;
    window.ResizeObserver = PatchedResizeObserver;
  }

  // (2) Backstop event swallowers.
  const isBenign = (event) => {
    const raw =
      (event && (event.message || event.reason?.message || event.reason)) || "";
    return String(raw).toLowerCase().includes("resizeobserver");
  };
  const swallow = (event) => {
    if (isBenign(event)) {
      event.stopImmediatePropagation?.();
      event.preventDefault?.();
    }
  };
  window.addEventListener("error", swallow, true);
  window.addEventListener("unhandledrejection", swallow, true);

  // (3) Filter console.error.
  const origConsoleError = window.console.error.bind(window.console);
  window.console.error = (...args) => {
    const first = args && args[0];
    const msg = typeof first === "string" ? first : first?.message || "";
    if (typeof msg === "string" && msg.toLowerCase().includes("resizeobserver")) {
      return;
    }
    origConsoleError(...args);
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
